/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
    Button,
    Dialog,
    DialogActions,
    Field,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Input,
    makeStyles,
    Textarea,
    mergeClasses,
} from '@fluentui/react-components';
import { Checkbox } from '@fluentui/react-components';
import { CalendarRegular } from '@fluentui/react-icons';
import { CaseEventTemplate, useInitialValues } from '../../data/types';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { Row, Column } from '../common';
import { useGuid } from '../../utilities/useGuid';
import { useValidation } from '../../data/types/validation';
import { useDialogControls } from './useDialogControls';

const useStyles = makeStyles({
    field: {
        width: '100%',
    },
});

type AddEventTemplateDialogProps = {
    children?: JSX.Element;
    onSubmit?: (eventTemplate: CaseEventTemplate) => Promise<void>;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    eventTemplateToEdit?: CaseEventTemplate;
};

export const AddEventTemplateDialog = (props: AddEventTemplateDialogProps) => {
    const { children, onSubmit, open, onOpenChange, eventTemplateToEdit } = props;

    //Hooks
    const initValues = useInitialValues();
    const guid = useGuid();
    const { validateCaseEventTemplate } = useValidation();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const fieldClasses = mergeClasses(styles.field, commonStyles.text.field);

    //Form
    const getEventTemplateValues = (): CaseEventTemplate => {
        if (!!eventTemplateToEdit) {
            return {
                ...eventTemplateToEdit,
            };
        } else {
            return initValues.caseEventTemplate();
        }
    };
    const eventTemplateForm = useFormik<CaseEventTemplate>({
        initialValues: getEventTemplateValues(),
        onSubmit: async (values, { resetForm }) => {
            if (!!values.eventName) {
                // Ensure we have an ID for new event templates
                if (!values.id) {
                    values.id = guid.newGuid();
                }

                !!onSubmit && (await onSubmit(values));
                resetForm({ values: getEventTemplateValues() });
            }
        },
        validationSchema: validateCaseEventTemplate.schema,
    });

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler] = useDialogControls(open, onOpenChange);

    //UI Events
    const clickSave = (e: any) => {
        e.stopPropagation();
        eventTemplateForm.submitForm();
    };
    const clickCancel = (e: any) => {
        e.stopPropagation();

        eventTemplateForm.resetForm({ values: getEventTemplateValues() });
    };

    //Effects
    useEffect(() => {
        if (dialogOpen && !!eventTemplateToEdit) {
            eventTemplateForm.resetForm({ values: getEventTemplateValues() });
        }
    }, [dialogOpen, eventTemplateToEdit]);

    return (
        <>
            <Dialog
                open={dialogOpen}
                onOpenChange={onOpenChangeHandler}
            >
                <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
                <DialogSurface aria-describedby={undefined} mountNode={undefined}>
                    <DialogBody>
                        <DialogTitle>
                            <Row gap='medium'>
                                <Column width='auto' vAlign='middle'>
                                    <CalendarRegular />
                                </Column>
                                <Column>{`${!!eventTemplateToEdit ? 'Edit' : 'Add'} Event`}</Column>
                            </Row>
                        </DialogTitle>
                        <DialogContent>
                            <Column>
                                <Field
                                    className={fieldClasses}
                                    label='Event Name'
                                    required
                                    validationMessage={eventTemplateForm.errors.eventName}
                                >
                                    <Input
                                        name='eventName'
                                        value={eventTemplateForm.values.eventName}
                                        onChange={eventTemplateForm.handleChange}
                                        placeholder='Enter the event name'
                                    />
                                </Field>
                                <Row>
                                    <Column>
                                        <Field className={fieldClasses} label='Days from Created' validationMessage={eventTemplateForm.errors.daysFromRetained}>
                                            <Input
                                                name='daysFromRetained'
                                                value={eventTemplateForm.values.daysFromRetained}
                                                onChange={eventTemplateForm.handleChange}
                                                placeholder='0'
                                            />
                                        </Field>
                                    </Column>
                                    <Column vAlign='bottom'>
                                        <Checkbox
                                            name='due'
                                            checked={eventTemplateForm.values.due}
                                            onChange={eventTemplateForm.handleChange}
                                            label='Is Due Date?'
                                        />
                                    </Column>
                                </Row>
                                <Field
                                    className={fieldClasses}
                                    label='Description'
                                    validationMessage={eventTemplateForm.errors.description}
                                    hint={`${eventTemplateForm.values.description?.length ?? 0} / ${validateCaseEventTemplate.description.maxLength}`}
                                >
                                    <Textarea
                                        name='description'
                                        value={eventTemplateForm.values.description}
                                        onChange={eventTemplateForm.handleChange}
                                        placeholder='Enter event description'
                                        maxLength={validateCaseEventTemplate.description.maxLength}
                                    />
                                </Field>
                            </Column>
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    type='submit'
                                    appearance='primary'
                                    onClick={clickSave}
                                    disabled={!eventTemplateForm.dirty || !eventTemplateForm.isValid}
                                    data-testid='event-template-dialog-save'
                                >
                                    Save Event Template
                                </Button>
                            </DialogTrigger>
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    appearance='secondary'
                                    onClick={clickCancel}
                                    data-testid='event-template-dialog-cancel'
                                >
                                    Cancel
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};
